import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, StatusBadge, EmptyState } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Copy, QrCode, Wallet, Zap, AlertCircle } from "lucide-react";
import DepositStatusCard from "@/components/DepositStatus";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";

export default function Deposit() {
  const { data: methodsData } = useFetch("/user/crypto-methods");
  const { data: depData, reload } = useFetch("/user/deposits");
  const { data: dash, reload: reloadDash } = useFetch("/user/dashboard");
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState("");
  const [txHash, setTxHash] = useState("");
  const [busy, setBusy] = useState(false);
  const [trackId, setTrackId] = useState(null);

  // activation
  const [actTarget, setActTarget] = useState("");
  const [actAmount, setActAmount] = useState("");
  const [actBusy, setActBusy] = useState(false);

  const methods = methodsData?.methods || [];
  const method = sel || methods[0];

  const submitDeposit = async (e) => {
    e.preventDefault();
    if (!method) return toast.error("No deposit method configured yet. Please contact support.");
    setBusy(true);
    try {
      const { data } = await api.post("/user/deposits", { coin: method.coin, network: method.network, amount: Number(amount), tx_hash: txHash });
      toast.success(data.auto ? "✅ On-chain confirmed & credited to wallet!" : (data.status === "pending_verification" ? "Deposit submitted — verifying on BSC…" : (data.message || "Deposit submitted for review")));
      if (data.deposit_id) setTrackId(data.deposit_id);
      setAmount(""); setTxHash(""); reload(); if (data.auto) reloadDash();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitActivation = async (e) => {
    e.preventDefault(); setActBusy(true);
    try {
      await api.post("/user/activate", { target_member_id: actTarget, amount: Number(actAmount) });
      toast.success("Activation successful"); setActTarget(""); setActAmount(""); reloadDash();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setActBusy(false); }
  };

  const submitRetop = async () => {
    if (!actAmount) return toast.error("Enter the retop amount");
    setActBusy(true);
    try {
      await api.post("/user/retop", { amount: Number(actAmount) });
      toast.success("Retop successful — new cycle started"); setActAmount(""); reloadDash();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setActBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="space-y-6">
      <PageTitle title="Deposit" subtitle="Fund your wallet with cryptocurrency" />

      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Wallet size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Make a Deposit</h3></div>
          {methods.length === 0 ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200">
              <AlertCircle size={18} /> No active deposit method is configured. Deposits are disabled until an administrator adds a crypto wallet.
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                {methods.map((m) => (
                  <button key={m.id} onClick={() => setSel(m)}
                    className={`btn3d ${method?.id === m.id ? "btn3d-gold" : "btn3d-dark"} px-4 py-2 text-xs`} data-testid={`method-${m.coin}`}>
                    {m.coin} · {m.network}
                  </button>
                ))}
              </div>
              <div className="rounded-xl glass-2 p-4">
                {method.qr_url ? (
                  <div className="mb-4 flex flex-col items-center">
                    <div className="rounded-2xl bg-white p-3 shadow-lg">
                      <img src={method.qr_url} alt="QR" className="h-44 w-44 rounded-lg object-contain" data-testid="deposit-qr" />
                    </div>
                    <p className="mt-3 flex items-center gap-1.5 text-base font-bold text-[var(--royal)]"><QrCode size={18} /> Scan to Pay</p>
                    <p className="text-xs text-[var(--text-dim)]">Scan this {method.coin} · {method.network} address with your wallet app</p>
                  </div>
                ) : (
                  <div className="mb-4 flex flex-col items-center text-[var(--text-dim)]"><QrCode size={44} /><p className="mt-1 text-xs">QR not uploaded yet — use the wallet address below</p></div>
                )}
                <div>
                  <p className="text-xs text-[var(--text-dim)]">Wallet Address ({method.network})</p>
                  <p className="mt-1 break-all text-sm text-white" data-testid="deposit-address">{method.address || "Not configured"}</p>
                </div>
                {method.address && <button onClick={() => copy(method.address)} className="mt-3 flex items-center gap-2 text-xs text-[var(--gold)]" data-testid="copy-address"><Copy size={14} /> Copy Address</button>}
                {method.instructions && <p className="mt-3 text-xs text-[var(--text-dim)]">{method.instructions}</p>}
              </div>
              <form onSubmit={submitDeposit} className="mt-4 space-y-3">
                <input className={inp} type="number" step="any" placeholder={`Amount (min ${methodsData?.min_deposit} USDT)`} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deposit-amount" required />
                <input className={inp} placeholder="Transaction hash (paste for instant on-chain confirmation)" value={txHash} onChange={(e) => setTxHash(e.target.value)} data-testid="deposit-hash" />
                <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="deposit-submit">Submit Deposit</LuxuryButton>
              </form>
            </>
          )}
        </GlassCard>

        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Zap size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Activate an ID</h3></div>
          <div className="mb-4 rounded-xl glass-2 p-4">
            <p className="text-xs text-[var(--text-dim)]">Available wallet balance</p>
            <p className="gold-text font-display text-2xl font-semibold" data-testid="wallet-balance">${money(dash?.balances?.deposit_balance || 0)}</p>
          </div>
          <form onSubmit={submitActivation} className="space-y-3">
            <input className={inp} placeholder="Member ID to activate (yours or another)" value={actTarget} onChange={(e) => setActTarget(e.target.value)} data-testid="activate-target" required />
            <input className={inp} type="number" step="any" placeholder="Package amount (qualifying business)" value={actAmount} onChange={(e) => setActAmount(e.target.value)} data-testid="activate-amount" required />
            <div className="flex gap-2">
              <LuxuryButton type="submit" className="flex-1" disabled={actBusy} data-testid="activate-submit">Activate</LuxuryButton>
              <LuxuryButton type="button" variant="maroon" className="flex-1" disabled={actBusy} onClick={submitRetop} data-testid="retop-submit">Retop (same ID)</LuxuryButton>
            </div>
          </form>
          <p className="mt-3 text-xs text-[var(--text-dim)]">Activation deducts from your wallet balance and credits commissions per program rules. Retop starts a new earning cycle on your same ID after the current one completes.</p>
        </GlassCard>
      </div>

      {trackId && <DepositStatusCard depositId={trackId} onDone={() => { reload(); reloadDash(); }} />}
    </div>
  );
}
